import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connectDjpal } from '../redux/thunk/djpalThunk';

const MAX_TRIES = 6;
// After the fast backoff is exhausted we keep trying slowly, so a token that
// lands late (cold-start hydration that outran the fast window) recovers
// automatically instead of leaving the error screen up forever.
const SLOW_RETRY_MS = 8000;
// Mid-session expiry of an ESTABLISHED token is not this hook's job: the
// app-level useDjpalTokenRenewal watches the JWT's exp and re-exchanges
// before it dies (this hook stops for good once tokenValid === true, and it
// only mounts in the Mixpedia-family tabs anyway).

/**
 * Establishes the DJPal token in the background once the user is authenticated.
 *
 * ROOT CAUSE of the App Review "Access token required" rejection: FeedTab (and
 * the other DJPal tabs) render BEHIND the login modal — App.js renders the tab
 * content with no `isAuthenticated` gate. So on a fresh install / logged-out
 * launch this hook fired `connectDjpal` while there was no token anywhere, the
 * exchange went out with no Authorization header, and the server returned
 * "Access token required". The old hook then exhausted its 6 tries (~7.5s) —
 * which easily elapses while the user is still completing the slow first-time
 * Sign in with Apple sheet — and never re-armed, so For You stayed stuck on the
 * error even after login landed.
 *
 * Fixes, in order of importance:
 *  1. GATE ON AUTH — never attempt (or surface an error) while unauthenticated.
 *     The token only exists once logged in / the session is restored, so a
 *     connect before that is guaranteed to fail. This alone removes the
 *     reviewer's trigger.
 *  2. RE-ARM ON AUTH — when the user becomes authenticated, restart the loop
 *     from scratch so a token that arrives after the fast window is used at
 *     once.
 *  3. SLOW BACKGROUND RETRY — after the fast tries, keep retrying so a
 *     late-hydrated token recovers without a manual tap.
 *
 * `connectFailed` is true only after the fast tries are exhausted AND the user
 * is authenticated, so the error UI never shows pre-login or during retries.
 */
export default function useDjpalConnect() {
    const dispatch = useDispatch();
    const tokenValid = useSelector((s) => s.djpal.tokenValid);
    const tokenError = useSelector((s) => s.djpal.tokenError);
    const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);
    const [tick, setTick] = useState(0);

    // Re-arm the loop from scratch whenever auth state changes — most importantly
    // the logged-out → logged-in transition, which is when the token first
    // becomes available.
    useEffect(() => {
        setTick(0);
    }, [isAuthenticated]);

    useEffect(() => {
        // Never connect (or error) until the user is authenticated: FeedTab
        // mounts behind the login modal, and a tokenless exchange there is the
        // App Review "Access token required" rejection.
        if (!isAuthenticated) return undefined;
        if (tokenValid === true) return undefined;
        const delay = tick === 0
            ? 0
            : tick < MAX_TRIES
                ? Math.min(500 * tick, 3000)
                : SLOW_RETRY_MS;
        const t = setTimeout(() => {
            dispatch(connectDjpal());
            setTick((c) => c + 1);
        }, delay);
        return () => clearTimeout(t);
    }, [isAuthenticated, tokenValid, tick, dispatch]);

    const connectFailed = isAuthenticated && tokenValid !== true && !!tokenError && tick >= MAX_TRIES;
    const retry = () => setTick(0);

    return { connectFailed, tokenError, retry };
}
